import React from 'react';
import {Tooltip} from 'react-bootstrap';
import getSpace from '../../model/contentful/get-space';

export const enrichTags = (tags, itemTags = []) => {
  // We only receive the tag ID from the API, so we need to enrich it with the name taken from the tags.
  const keyedTags = keyMapByField(tags, 'id', 'name');

  const enrichedTags = itemTags.reduce((res, tag) => {
    const name = keyedTags[tag.id] ?? null;
    if (name === null) {
      return res;
    }

    res.push({
      id: tag.id,
      name: name
    });

    return res;
  }, []);

  return enrichedTags;
};

/**
 * Create a map keyed by a specific field.
 * If valueField is set then that field will be used as value,
 * otherwise the entire object is placed on the given key.
 *
 * @param {Array} tags
 * @param {string} field
 * @param {string} valueField
 * @returns Object
 */
export const keyMapByField = (tags, field = 'id', valueField = null) => {
  const keyedTags = tags.reduce((res, item) => {
    res[item[field]] = valueField ? item[valueField] : item;

    return res;
  }, {});

  return keyedTags;
};

export const mapContentType = (contentType) => {
  const types = {
    make: 'Bilmerke',
    bilmerker: 'Bilmerke',
    campaign: 'Kampanje',
    forhandler: 'Forhandler',
    employee: 'Ansatt',
    equipmentLevel: 'Utstyrsnivå',
    equipment: 'Tilbehør',
    equipmentFactory: 'Ekstrautstyr',
    model: 'Bilmodell',
    variant: 'Variant',
    variantGroup: 'Variantgruppe',
    color: 'Farge',
    generation: 'Generasjon',
    lease: 'Lease',
    warranty: 'Garantivilkår',
    leadSchema: 'Skjema',
    openGraph: 'openGraph',
    articlecollection: 'Artikkelkolleksjon'
  };
  if (types[contentType]) {
    return types[contentType];
  }
  return contentType;
};

export const mapMakeToMakeId = {
  mitsubishi: '3VbY7QCgG42YqysyYKOOGM',
  renault: '40gRCS9YR26cMgssuiK0G4',
  dacia: '6hVJ98ORXysk28QwIQosiM',
  hongqi: '791tr5d9gns4TrC90iHfk1',
  xpeng: '4taEzhTNrFvCCnZlkida6p'
};

const baseUrls = {
  make: '/modeller/',
  bilmerker: '/merker/',
  campaign: '/campaigns/',
  forhandler: '/forhandler/',
  employee: '/Ansatt/',
  equipmentLevel: '/equipmentlevel/',
  equipment: '/equipment/',
  equipmentFactory: '/equipmentFactory/',
  model: '/modell/',
  variant: '/variant/',
  variantGroup: '/variantgroup/',
  lease: '/lease/',
  leadSchema: '/leadSchemas/',
  openGraph: '/opengraph/',
  generation: '/generation/',
  articlecollection: '/articles/'
};

export const generateEditLink = (contentType, id) => {
  const url = baseUrls[contentType] + id;
  return url;
};

export const generateEditLinkWithAutoResolve = async (contentType, id, name = '', space = null) => {
  if (baseUrls[contentType] !== undefined) {
    return {
      id,
      url: baseUrls[contentType] + id,
      name: name
    };
  }

  // We do not have a record of the content type so it is most likely an item that does not have
  // a direct link (like imageBlock), so we need to resolve links up to one we have. A case that might
  // happen is that we just have not updated baseUrls to contain the new link, so that needs to be handled.
  if (space === null) {
    space = await getSpace();
  }

  // No content type match, so time to attempt going further.
  const linkedEntries = await space.getEntries({
    links_to_entry: id
  });

  for (let i = 0; i < linkedEntries.items.length; i++) {
    const linkedEntry = linkedEntries.items[i];

    try {
      const entry = await space.getEntry(linkedEntry.sys.id);
      const foundEntry = await generateEditLinkWithAutoResolve(
        entry.sys.contentType.sys.id,
        entry.sys.id,
        entry.fields.displayname?.['nb-NO'] ?? entry.fields.name?.['nb-NO'] ?? entry.fields.title?.['nb-NO'] ?? 'Ukjent navn',
        space
      );

      return foundEntry;
    } catch (error) {
      throw error;
    }
  }

  // If we have not found anything till now, its most likely a content type we haven't handled yet, so throw an error.
  throw new Error('Ukjent entitetstype funnet.');
};

export const createTooltip = (text) => {
  const tooltip = (
    <Tooltip id="tooltip-required-id">
      {text}
    </Tooltip>
  );

  return tooltip;
};
